import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { FaArrowLeft, FaArrowRight, FaClock, FaCalendarAlt } from 'react-icons/fa';
import { setSelectedTime } from '../../redux/slices/tiffinSlice';
import { getColors } from '../../styles/colors';

const TimeSelect = () => {
  const dispatch = useDispatch();
  const { selectedDate, selectedTime, orderItems } = useSelector((state) => state.tiffin);
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const hasItems = orderItems.length > 0;

  const timeSlots = [];
  for (let i = 8; i <= 22; i++) {
    const hour = i > 12 ? i - 12 : i;
    const ampm = i >= 12 ? 'PM' : 'AM';
    timeSlots.push(`${hour}:00 ${ampm}`);
    timeSlots.push(`${hour}:30 ${ampm}`);
  }

  const handleTimeSelect = (time) => {
    dispatch(setSelectedTime(time));
  };

  // Group items by date
  const groupedByDate = orderItems.reduce((acc, item) => {
    const date = item.scheduledDate || 'Unassigned';
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  if (!hasItems || !selectedDate) {
    return (
      <motion.div 
        className="py-4 px-4 max-w-4xl mx-auto"
        style={{ backgroundColor: colors.background }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-4 mb-6">
          <Link to="/date-select" className="text-2xl" style={{ color: colors.text }}>
            <FaArrowLeft />
          </Link>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>⏰ Select Time</h1>
        </div>
        <div 
          className="rounded-2xl p-12 text-center"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
        >
          <p className="text-4xl mb-4">⏰</p>
          <p className="text-lg" style={{ color: colors.textSecondary }}>
            {!hasItems ? 'No items in your order' : 'Please select a date first'}
          </p>
          <Link to={!hasItems ? "/meal-select" : "/date-select"}>
            <button 
              className="mt-4 px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
              style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}
            >
              {!hasItems ? 'Browse Meals' : 'Select Date'}
            </button>
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="py-4 px-4 max-w-4xl mx-auto"
      style={{ backgroundColor: colors.background }}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center gap-4 mb-6">
        <Link to="/date-select" className="text-2xl" style={{ color: colors.text }}>
          <FaArrowLeft />
        </Link>
        <h1 className="text-2xl font-bold" style={{ color: colors.text }}>⏰ Select Time</h1>
      </div>

      {/* Date Summary */}
      <div 
        className="rounded-2xl p-4 mb-6"
        style={{ 
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`
        }}
      >
        <div className="flex items-center gap-3">
          <FaCalendarAlt style={{ color: colors.primary }} />
          <span style={{ color: colors.text }}>
            {Object.keys(groupedByDate).map(date => 
              date === 'Unassigned' ? 'Unassigned' : new Date(date).toLocaleDateString()
            ).join(' • ')}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-2 text-sm" style={{ color: colors.textSecondary }}>
          <FaClock />
          <span>Select delivery time for all meals</span>
        </div>
      </div>

      {/* Time Slots */}
      <div 
        className="rounded-2xl p-6"
        style={{ 
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`
        }}
      >
        <div className="flex items-center gap-2 mb-4" style={{ color: colors.textSecondary }}>
          <FaClock style={{ color: colors.primary }} />
          <span>Available delivery slots</span>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-60 overflow-y-auto">
          {timeSlots.map((time) => (
            <button
              key={time}
              className={`py-3 px-2 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 ${
                selectedTime === time ? 'active' : ''
              }`}
              style={{ 
                backgroundColor: selectedTime === time ? colors.primary : colors.inputBackground,
                color: selectedTime === time ? '#FFFFFF' : colors.text,
                border: `1px solid ${selectedTime === time ? colors.primary : colors.border}`
              }}
              onClick={() => handleTimeSelect(time)}
            >
              {time}
            </button>
          ))}
        </div>
      </div>

      {/* Selected Items Summary */}
      <div 
        className="rounded-2xl p-4 mt-6"
        style={{ 
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`
        }}
      >
        <h3 className="font-bold mb-2" style={{ color: colors.text }}>📋 Order Summary</h3>
        {Object.entries(groupedByDate).map(([date, items]) => (
          <div key={date} className="mb-2">
            <p className="text-sm font-medium" style={{ color: colors.primary }}>
              📅 {date === 'Unassigned' ? 'Unassigned' : new Date(date).toLocaleDateString()}
            </p>
            {items.map(item => (
              <div key={item.id} className="flex justify-between text-sm py-1" style={{ color: colors.textSecondary }}>
                <span>{item.name} x{item.quantity}</span>
                <span style={{ color: colors.primary }}>₹{item.price * item.quantity}</span>
              </div>
            ))}
          </div>
        ))}
        <div className="border-t pt-2 mt-2" style={{ borderColor: colors.border }}>
          <div className="flex justify-between font-bold" style={{ color: colors.text }}>
            <span>Total</span>
            <span style={{ color: colors.primary }}>₹{orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mt-6">
        <Link to="/date-select" className="btn-secondary">
          ← Back
        </Link>
        <Link to="/payment">
          <button className="btn-primary">
            Proceed to Payment <FaArrowRight className="inline ml-2" />
          </button>
        </Link>
      </div>
    </motion.div>
  );
};

export default TimeSelect;